import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

/**
 * FestivalThemeProvider — connects the storefront to the backend-managed
 * theme system. On load it asks the server which theme is active (manual
 * override → scheduled window → built-in default) and applies the theme's
 * validated design tokens by injecting a scoped <style> tag. The user's
 * light/dark preference (html.light) is untouched — festival themes style
 * BOTH appearance modes and never override the visitor's choice.
 *
 * Safety: tokens arrive as structured, server-validated colors mapped onto a
 * fixed whitelist of CSS variables. No arbitrary CSS/JS is ever injected.
 * If the theme API is unreachable we render the built-in PAQVERO default.
 */
const FestivalThemeContext = createContext({ theme: null, isFestival: false, preview: false });

export const useFestivalTheme = () => useContext(FestivalThemeContext);

const TOKEN_VAR = {
  bg: "--pq-bg", surface: "--pq-surface", surface2: "--pq-surface2", elev: "--pq-elev",
  text1: "--pq-text1", text2: "--pq-text2", text3: "--pq-text3",
  line: "--pq-line", line2: "--pq-line2", line_strong: "--pq-line-strong",
  accent: "--pq-accent", accent_hover: "--pq-accent-hover",
  accent_soft: "--pq-accent-soft", hover: "--pq-hover",
};

const COLOR_RE = /^(#[0-9a-fA-F]{3,8}|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(,\s*(0|1|0?\.\d{1,4})\s*)?\))$/;

function buildCss(theme) {
  const block = (tokens, selector) => {
    const decls = Object.entries(TOKEN_VAR)
      .filter(([k]) => COLOR_RE.test(String(tokens?.[k] || "")))
      .map(([k, cssVar]) => `${cssVar}: ${tokens[k]};`)
      .join(" ");
    return decls ? `${selector} { ${decls} }` : "";
  };
  return `${block(theme.tokens_dark, ":root")} ${block(theme.tokens_light, "html.light")}`;
}

export function FestivalThemeProvider({ children }) {
  const [state, setState] = useState({ theme: null, preview: false });

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const previewId = params.get("pq_theme_preview");
    const req = previewId
      ? api.get(`/shop/theme/preview/${previewId}`, { params: { exp: params.get("exp"), key: params.get("key") } })
      : api.get("/shop/theme/active");
    req
      .then(({ data }) => {
        if (cancelled) return;
        if (data.default || !data.theme) {
          document.documentElement.dataset.pqFestiveFont = "";
          document.documentElement.dataset.pqFestival = "";
          setState({ theme: null, preview: false });
          return;
        }
        const css = buildCss(data.theme);
        let tag = document.getElementById("pq-festival-theme");
        if (!tag) {
          tag = document.createElement("style");
          tag.id = "pq-festival-theme";
          document.head.appendChild(tag);
        }
        tag.textContent = css;
        // Festive typography opt-in (validated enum — only known font stacks)
        // Legacy "brunson" configs are upgraded to the approved Diwali pairing
        // (Fraunces + Inter) so the live site matches preview typography.
        let displayFont = data.theme.content?.display_font || "default";
        if (displayFont === "brunson") displayFont = "diwali";
        if (displayFont === "festive" && !document.getElementById("pq-festive-font")) {
          const link = document.createElement("link");
          link.id = "pq-festive-font";
          link.rel = "stylesheet";
          link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,600;0,700;1,500&display=swap";
          document.head.appendChild(link);
        }
        if (displayFont === "brunson" && !document.getElementById("pq-brunson-font")) {
          // Self-hosted vintage display face — no external dependency
          const st = document.createElement("style");
          st.id = "pq-brunson-font";
          st.textContent = "@font-face{font-family:'Brunson';src:url('/fonts/brunson.ttf') format('truetype');font-display:swap;}";
          document.head.appendChild(st);
        }
        if (displayFont === "diwali" && !document.getElementById("pq-diwali-font")) {
          // Diwali pairing: Fraunces is already global; Inter loads on demand
          // (display=swap keeps text visible during fetch — no layout shift).
          const link = document.createElement("link");
          link.id = "pq-diwali-font";
          link.rel = "stylesheet";
          link.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
          document.head.appendChild(link);
        }
        document.documentElement.dataset.pqFestiveFont = ["festive", "brunson", "diwali"].includes(displayFont) ? displayFont : "";
        document.documentElement.dataset.pqFestival = "true";
        setState({ theme: data.theme, preview: Boolean(data.preview) });
      })
      .catch(() => {
        if (!cancelled) setState({ theme: null, preview: false }); // safe fallback
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({
    theme: state.theme,
    isFestival: Boolean(state.theme),
    preview: state.preview,
    content: state.theme?.content || {},
    sparkles: state.theme?.sparkles || null,
    decor: state.theme?.decor || {},
    ambientGlow: state.theme ? state.theme.ambient_glow !== false : true,
  }), [state]);

  return <FestivalThemeContext.Provider value={value}>{children}</FestivalThemeContext.Provider>;
}
